import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import BaseDatePicker from "../../Base/BaseDatePicker";
import useServicesService from "../../../services/useServicesService";
import useCompaniesService from "../../../services/useCompaniesService";
import showToast from "../../../utils/toast";
import type { ServiceResponse } from "../../../hooks/useFetch";
import "./styles/ServicesPlanillaExportModal.css";

interface ServicesPlanillaExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PlanillaForm {
  company_id: string;
  start_date: string;
  end_date: string;
}

const ALL_COMPANIES = "all";

const ServicesPlanillaExportModal: React.FC<ServicesPlanillaExportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { downloadServicesPlanilla, fetching } = useServicesService();
  const { list: listCompanies } = useCompaniesService();
  const [companies, setCompanies] = useState<Array<{ value: string; label: string }>>([]);
  const [form, setForm] = useState<PlanillaForm>({
    company_id: ALL_COMPANIES,
    start_date: "",
    end_date: "",
  });
  const [error, setError] = useState("");

  const companyOptions = useMemo(
    () => [{ value: ALL_COMPANIES, label: "Todas las empresas (una hoja c/u)" }, ...companies],
    [companies]
  );

  useEffect(() => {
    if (!isOpen) return;

    listCompanies({}, {
      onSuccess: (data: ServiceResponse) => {
        if (data?.data?.companies) {
          setCompanies(
            data.data.companies.map((c: { id: number; name: string }) => ({
              value: String(c.id),
              label: c.name,
            }))
          );
        }
      },
      onError: () => {
        setError("No se pudieron cargar las empresas");
      },
    });
  }, [isOpen, listCompanies]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setForm({ company_id: ALL_COMPANIES, start_date: "", end_date: "" });
      setError("");
    }
  }, [isOpen]);

  const handleChange = (name: keyof PlanillaForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleDownload = async () => {
    if (!form.company_id || !form.start_date || !form.end_date) {
      setError("Complete empresa y rango de fechas");
      return;
    }

    if (new Date(form.start_date) > new Date(form.end_date)) {
      setError("La fecha de inicio debe ser anterior a la fecha de fin");
      return;
    }

    const companyArg =
      form.company_id === ALL_COMPANIES ? ("all" as const) : Number(form.company_id);

    await downloadServicesPlanilla(companyArg, form.start_date, form.end_date, {
      onSuccess: () => {
        showToast(
          form.company_id === ALL_COMPANIES
            ? "Excel completo descargado (hojas por empresa)"
            : "Planilla descargada correctamente",
          "success"
        );
        onClose();
      },
      onError: (err) => {
        setError(err.message || "Error al descargar la planilla");
      },
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="services-planilla-modal-overlay" onClick={onClose}>
      <div
        className="services-planilla-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="services-planilla-title"
      >
        <header className="services-planilla-modal__header">
          <div>
            <h2 id="services-planilla-title">Descargar servicios Excel</h2>
            <p>
              Formato completo con conductor. El Abra agrega Gerencia / Superintendencia / Area /
              Responsable; ENAEX agrega Codigo Enaex. Si hay notas, se agregan Nota 1 / Nota 2.
              Con &quot;Todas las empresas&quot; se crea una hoja por empresa.
            </p>
          </div>
          <button
            type="button"
            className="services-planilla-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </header>

        <div className="services-planilla-modal__body">
          <div className="services-planilla-modal__field">
            <BaseSelect
              label="Empresa"
              name="company_id"
              value={form.company_id}
              onChange={(e) => handleChange("company_id", e.target.value)}
              options={companyOptions}
              placeholder="Seleccionar empresa"
            />
          </div>

          <div className="services-planilla-modal__dates">
            <div className="services-planilla-modal__field">
              <BaseDatePicker
                label="Fecha de inicio"
                name="start_date"
                value={form.start_date}
                onChange={(e) => handleChange("start_date", e.target.value)}
              />
            </div>
            <div className="services-planilla-modal__field">
              <BaseDatePicker
                label="Fecha de fin"
                name="end_date"
                value={form.end_date}
                onChange={(e) => handleChange("end_date", e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="services-planilla-modal__error" role="alert">
              {error}
            </div>
          )}
        </div>

        <footer className="services-planilla-modal__footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-download"
            onClick={handleDownload}
            disabled={fetching.servicesPlanillaDownload}
          >
            <Download size={16} />
            {fetching.servicesPlanillaDownload ? "Descargando..." : "Descargar Excel"}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default ServicesPlanillaExportModal;
